/**
 * dinnerPracticeService.js
 * Serves Family Dinner Practices per grade/level and skill.
 * IMPORTANT: No em-dashes are used anywhere in the content.
 */

const BASE_URL = process.env.REACT_APP_FIREBASE_DATABASE_URL;

export const DEFAULT_DINNER_PRACTICES = {
  grade1: [
    {
      id: "g1_add_1",
      grade: 1,
      skill: "addition",
      topicLabel: "TONIGHT'S DINNER-TABLE QUESTION . PRACTICES ADDITION WITHOUT SCREENS",
      question: '"If we have 3 spoons and 4 forks on the table, how many utensils do we have in total?"',
      followUp: "What if we add 2 napkins?",
      hint: "Let your child count the items one by one on the table."
    },
    {
      id: "g1_sub_1",
      grade: 1,
      skill: "subtraction",
      topicLabel: "TONIGHT'S DINNER-TABLE QUESTION . PRACTICES SUBTRACTION WITHOUT SCREENS",
      question: '"There were 7 carrot sticks on the plate. You ate 3. How many carrot sticks are left?"',
      followUp: "What happens if you eat 2 more?",
      hint: "Use actual food pieces on the plate to show taking away."
    },
    {
      id: "g1_word_1",
      grade: 1,
      skill: "word_problems",
      topicLabel: "TONIGHT'S DINNER-TABLE QUESTION . PRACTICES COUNTING AND FOCUS",
      question: '"We have 4 people sitting at dinner. If everyone gets 1 glass of water, how many glasses do we need?"',
      followUp: "How many glasses if grandma and grandpa join us?",
      hint: "Point to each person seated at the table."
    }
  ],
  grade2: [
    {
      id: "g2_div_1",
      grade: 2,
      skill: "division",
      topicLabel: "TONIGHT'S DINNER-TABLE QUESTION . PRACTICES DIVISION WITHOUT SCREENS",
      question: '"If we share 12 cookies among 4 people, how many does everyone get?"',
      followUp: "What if we share 12 cookies among 3 people instead?",
      hint: "Deal forks or napkins like cards to visualize equal sharing."
    },
    {
      id: "g2_add_1",
      grade: 2,
      skill: "addition",
      topicLabel: "TONIGHT'S DINNER-TABLE QUESTION . PRACTICES ADDITION WITHOUT SCREENS",
      question: '"Mom cooked 15 meatballs and Dad cooked 12 meatballs. How many meatballs do we have for dinner?"',
      followUp: "How many more meatballs did Mom cook than Dad?",
      hint: "Break 15 + 12 into tens (10 + 10 = 20) and ones (5 + 2 = 7)."
    },
    {
      id: "g2_sub_1",
      grade: 2,
      skill: "subtraction",
      topicLabel: "TONIGHT'S DINNER-TABLE QUESTION . PRACTICES SUBTRACTION WITHOUT SCREENS",
      question: '"We baked 20 mini rolls. Our family ate 8 of them. How many rolls are saved for tomorrow?"',
      followUp: "If we eat 4 more for breakfast, how many remain?",
      hint: "Count back from 20 or use 20 - 10 + 2 = 12."
    },
    {
      id: "g2_mul_1",
      grade: 2,
      skill: "multiplication",
      topicLabel: "TONIGHT'S DINNER-TABLE QUESTION . PRACTICES MULTIPLICATION WITHOUT SCREENS",
      question: '"There are 4 people at the table and each person has 2 napkins. How many napkins in total?"',
      followUp: "How many napkins if each person had 3?",
      hint: "Skip count by twos: 2, 4, 6, 8."
    }
  ],
  grade3: [
    {
      id: "g3_div_1",
      grade: 3,
      skill: "division",
      topicLabel: "TONIGHT'S DINNER-TABLE QUESTION . PRACTICES DIVISION WITHOUT SCREENS",
      question: '"We have 24 apple slices to share equally among 6 people. How many slices does each person get?"',
      followUp: "What if only 4 people want apple slices?",
      hint: "Think about multiplication facts: what times 6 equals 24?"
    },
    {
      id: "g3_mul_1",
      grade: 3,
      skill: "multiplication",
      topicLabel: "TONIGHT'S DINNER-TABLE QUESTION . PRACTICES MULTIPLICATION WITHOUT SCREENS",
      question: '"If a muffin tin holds 3 rows of 4 muffins, how many muffins can we bake at once?"',
      followUp: "What if there are 4 rows of 5 muffins?",
      hint: "Visualize an array grid of rows and columns."
    },
    {
      id: "g3_frac_1",
      grade: 3,
      skill: "fractions",
      topicLabel: "TONIGHT'S DINNER-TABLE QUESTION . PRACTICES FRACTIONS WITHOUT SCREENS",
      question: '"We cut a pizza into 8 equal slices. If you eat 2 slices, what fraction of the pizza did you eat?"',
      followUp: "Can we simplify two eighths to one fourth?",
      hint: "Draw a circle in the air or point to round plates."
    }
  ],
  grade4: [
    {
      id: "g4_div_1",
      grade: 4,
      skill: "division",
      topicLabel: "TONIGHT'S DINNER-TABLE QUESTION . PRACTICES DIVISION WITHOUT SCREENS",
      question: '"The grocery bill was 48 dollars for 4 bags of groceries. What was the average cost per bag?"',
      followUp: "If we bought 6 bags for 72 dollars, is the average cost the same?",
      hint: "Divide 40 by 4 and 8 by 4 separately, then add."
    },
    {
      id: "g4_mul_1",
      grade: 4,
      skill: "multiplication",
      topicLabel: "TONIGHT'S DINNER-TABLE QUESTION . PRACTICES MULTIPLICATION WITHOUT SCREENS",
      question: '"We bought 12 packs of juice boxes and each pack has 6 boxes. How many juice boxes do we have?"',
      followUp: "How many juice boxes in 15 packs?",
      hint: "Multiply 10 x 6 = 60 and 2 x 6 = 12, then sum them."
    },
    {
      id: "g4_frac_1",
      grade: 4,
      skill: "fractions",
      topicLabel: "TONIGHT'S DINNER-TABLE QUESTION . PRACTICES FRACTIONS WITHOUT SCREENS",
      question: '"One bowl has 1/2 liter of soup and another has 1/4 liter. How much soup do we have together?"',
      followUp: "How much more soup is in the first bowl than the second?",
      hint: "Convert 1/2 to 2/4 before adding."
    }
  ],
  grade5: [
    {
      id: "g5_div_1",
      grade: 5,
      skill: "division",
      topicLabel: "TONIGHT'S DINNER-TABLE QUESTION . PRACTICES DIVISION WITHOUT SCREENS",
      question: '"If 3 families share the cost of a 75 dollar dinner equally, how much does each family pay?"',
      followUp: "What if the total bill was 90 dollars?",
      hint: "Break 75 into 60 and 15."
    },
    {
      id: "g5_word_1",
      grade: 5,
      skill: "word_problems",
      topicLabel: "TONIGHT'S DINNER-TABLE QUESTION . PRACTICES REAL WORLD MATH WITHOUT SCREENS",
      question: '"A recipe calls for 2.5 cups of flour for one loaf of bread. How many cups for 4 loaves?"',
      followUp: "How many cups for 10 loaves?",
      hint: "Double 2.5 to get 5 for two loaves, then double again."
    }
  ]
};

/**
 * Fetch dinner practices for a specific grade and optional skill.
 * Reads from Firebase DB or falls back to DEFAULT_DINNER_PRACTICES.
 */
export const getDinnerPracticesForGrade = async (gradeNum, targetSkill = null) => {
  const gradeKey = `grade${gradeNum || 2}`;
  let practices = DEFAULT_DINNER_PRACTICES[gradeKey] || DEFAULT_DINNER_PRACTICES.grade2;

  if (BASE_URL) {
    try {
      const res = await fetch(`${BASE_URL}/familyDinnerPractices/${gradeKey}.json`);
      if (res.ok) {
        const remoteData = await res.json();
        if (Array.isArray(remoteData) && remoteData.length > 0) {
          practices = remoteData;
        }
      }
    } catch (e) {
      // Fallback to local default practices
    }
  }

  // Filter by skill if requested and matches exist
  if (targetSkill) {
    const matching = practices.filter(p => p.skill.toLowerCase() === targetSkill.toLowerCase());
    if (matching.length > 0) {
      return matching;
    }
  }

  return practices;
};

/**
 * Sync default dinner practices to Firebase if connected
 */
export const syncDinnerPracticesToFirebase = async () => {
  if (!BASE_URL) return;
  try {
    await fetch(`${BASE_URL}/familyDinnerPractices.json`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(DEFAULT_DINNER_PRACTICES)
    });
  } catch (e) {
    console.error("Could not sync dinner practices to Firebase:", e);
  }
};
